import { Typography } from '@material-ui/core';
import { FormikTextField, useFormikEnhanced } from '@superdispatch/forms';
import { Stack, useSnackbarStack } from '@superdispatch/ui';
import { useEffect, useMemo } from 'react';
import { APIResponse } from 'shared/api/CarrierAPIClient';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import { useInitialValues } from 'shared/form/AppFormik';
import { toFormikValidationSchema } from 'shared/form/FormSchema';
import { useFlaggedSchema } from 'shared/hooks/useFlaggedSchema';
import { DocumentTitle } from 'shared/layout/DocumentTitle';
import { trackCarrierProfileEvent } from 'shared/modules/carrier-profile/data/CarrierProfileAnalytics';
import {
  useBillingInfo,
  useCarrierProfileAPI,
} from 'shared/modules/carrier-profile/data/CarrierProfileAPI';
import {
  BillingInfoDTO,
  billingInfoSchema,
  billingInfoYupSchema,
} from 'shared/modules/carrier-profile/data/CarrierProfileDTO';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import { ProfilePageLayout } from './ProfilePageLayout';
import { PermissionView } from 'shared/modules/permission/core/PermissionView';
import { PermissionButton } from 'shared/modules/permission/core/PermissionButton';

export function ProfileBillingInfoPage() {
  const { data, error, isLoading } = useBillingInfo();
  const { updateBillingInfo } = useCarrierProfileAPI();
  const { addSnackbar } = useSnackbarStack();
  const schema = useFlaggedSchema(
    'ctms_zod_schema',
    billingInfoYupSchema,
    billingInfoSchema,
  );
  const initialValues = useInitialValues(schema, data);
  const validationSchema = useMemo(
    () => toFormikValidationSchema(schema),
    [schema],
  );
  const { data: settings } = useCarrierSettings();

  const isEditingDisabled =
    !settings?.drivers_can_edit_carrier_info && MobileAppBridge.isInjected();

  const formik = useFormikEnhanced<BillingInfoDTO, APIResponse>({
    initialValues,
    validationSchema,
    onSubmit: updateBillingInfo,
    onSubmitSuccess: () => {
      addSnackbar('Billing info was successfully updated', {
        variant: 'success',
      });

      trackCarrierProfileEvent({
        name: 'Carrier Updated Carrier Info',
        utm_medium: 'Carrier Profile',
        utm_content: 'Billing Info',
      });
    },
    onSubmitFailure: ({ message }) =>
      addSnackbar(`Failed to update billing info: ${message}`, {
        variant: 'error',
      }),
  });

  useEffect(() => {
    if (error) {
      addSnackbar('Failed to fetch billing info', { variant: 'error' });
    }
  }, [addSnackbar, error]);

  return (
    <>
      <DocumentTitle title="Billing Info - Profile" />

      <PermissionView name="billing_info" permission="can_view">
        <ProfilePageLayout
          formik={formik}
          title="Billing Info"
          isLoading={isLoading}
          actions={
            <PermissionButton
              name="billing_info"
              permission="can_edit"
              pending={formik.isSubmitting}
              disabled={!formik.dirty}
              onClick={() => {
                void formik.submitForm();
              }}
            >
              Save
            </PermissionButton>
          }
        >
          <Stack space="small">
            <Typography variant="h4">Billing</Typography>

            <FormikTextField
              label="Name"
              name="billing_contact_name"
              fullWidth={true}
              disabled={isEditingDisabled}
            />

            <FormikTextField
              label="Phone Numbers"
              name="billing_phone_numbers"
              fullWidth={true}
              disabled={isEditingDisabled}
            />

            <FormikTextField
              label="Email"
              name="billing_email"
              fullWidth={true}
              disabled={isEditingDisabled}
            />
          </Stack>
        </ProfilePageLayout>
      </PermissionView>
    </>
  );
}
